import { useEffect, useMemo, useState } from "react";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import employeeJobWorkApi from "../api/employeeJobWorkApi";
import contractorLedgerApi from "../api/contractorLedgerApi";
import hundekariLedgerApi from "../api/hundekariLedgerApi";
import transportLedgerApi from "../api/transportLedgerApi";
import vendorLedgerApi from "../api/vendorLedgerApi";
import ledgerHubApi from "../api/ledgerHubApi";
import vendorApi from "../api/vendorApi";
import customerApi from "../api/customerApi";
import Card from "../components/Card";
import Btn from "../components/Btn";
import Input from "../components/Input";
import Select2 from "../components/Select2";
import PageHeader from "../components/PageHeader";
import T from "../theme/colors";

const money = v => `\u20B9${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
const fmtDate = d => d ? new Date(d + (String(d).length === 10 ? "T00:00:00" : "")).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" }) : "—";
const num = v => Number(v || 0);

const TYPE_LABELS = { CUSTOMER: "Customer", STAFF: "Staff", CONTRACTOR: "Contractor", HUNDEKARI: "Hundekari", TRANSPORT: "Transport", VENDOR: "Vendor" };

/**
 * Ledger Hub — every party's ledger behind one dropdown, with the
 * pending work counted against every name.
 *
 * Multi-select: pick as many parties as you like (across types) and each
 * gets its OWN section — opening, total credit, total debit and closing
 * over that party's rows, with the ledger beneath it. A grand strip on
 * top adds the sections up. Nothing is merged into a single stream: two
 * parties' balances are different running accounts and adding their rows
 * together would produce a balance column that means nothing.
 */
export default function LedgerHubPage() {
    const { hasPermission } = useAuth();

    const canCustomer = hasPermission("CUSTOMER", "VIEW");
    const canStaff = hasPermission("EMPLOYEE_LEDGER", "VIEW");
    const canContractor = hasPermission("CONTRACTOR_LEDGER", "VIEW");
    const canHundekari = hasPermission("HUNDEKARI_LEDGER", "VIEW");
    const canTransport = hasPermission("TRANSPORT_LEDGER", "VIEW");
    const canVendor = hasPermission("VENDOR_LEDGER", "VIEW");

    const { data: cusS } = useApi(customerApi.getAll, [], { cacheKey: "lhCus" });
    const { data: empS } = useApi(employeeJobWorkApi.getSummary, [], { cacheKey: "hubEmp" });
    const { data: conS } = useApi(contractorLedgerApi.getSummary, [], { cacheKey: "hubCon" });
    const { data: hunS } = useApi(hundekariLedgerApi.getSummary, [], { cacheKey: "hubHun" });
    const { data: traS } = useApi(transportLedgerApi.getSummary, [], { cacheKey: "hubTra" });
    const { data: venS } = useApi(vendorApi.getAll, [], { cacheKey: "hubVen" });
    const { data: pendS } = useApi(ledgerHubApi.pendingCounts, [], { cacheKey: "lhPend" });
    const arr = v => (Array.isArray(v) ? v : []);
    const pend = pendS && typeof pendS === "object" ? pendS : {};
    const pAt = (bucket, id) => (pend[bucket] || {})[String(id)] || null;

    const pendingText = (type, id) => {
        if (type === "VENDOR") {
            const b = pAt("vendorBills", id);
            return b ? `${b.count} bill${b.count > 1 ? "s" : ""} due` : null;
        }
        if (type === "STAFF") {
            const jw = pAt("employeeJw", id), sl = pAt("employeeSlips", id);
            const parts = [];
            if (jw) parts.push(`${jw.count} JW`);
            if (sl) parts.push(`${sl.count} slip${sl.count > 1 ? "s" : ""}`);
            return parts.length ? parts.join(" + ") + " pending" : null;
        }
        if (type === "CONTRACTOR") {
            const jw = pAt("contractorJw", id);
            return jw ? `${jw.count} JW pending` : null;
        }
        return null;
    };
    const pendingAmount = (type, id) => {
        if (type === "VENDOR") return num(pAt("vendorBills", id)?.amount);
        if (type === "STAFF") return num(pAt("employeeJw", id)?.amount) + num(pAt("employeeSlips", id)?.amount);
        if (type === "CONTRACTOR") return num(pAt("contractorJw", id)?.amount);
        return 0;
    };

    const TYPES = [
        canCustomer && { key: "CUSTOMER", label: "Customer" },
        canStaff && { key: "STAFF", label: "Staff" },
        canContractor && { key: "CONTRACTOR", label: "Contractor" },
        canHundekari && { key: "HUNDEKARI", label: "Hundekari" },
        canTransport && { key: "TRANSPORT", label: "Transport" },
        canVendor && { key: "VENDOR", label: "Vendor" },
    ].filter(Boolean);
    // MULTI type filter - tick Staff + Contractor + Vendor together and each
    // gets its own section below with the whole book's Debit / Credit / Balance.
    const [fTypes, setFTypes] = useState([]);
    const { data: totS } = useApi(ledgerHubApi.typeTotals, [], { cacheKey: "lhTot" });
    const typeTotals = totS && typeof totS === "object" ? totS : {};

    const allParties = useMemo(() => [
        ...(canCustomer ? arr(cusS).map(c => ({ type: "CUSTOMER", id: c.id, name: c.customerName, bal: null })) : []),
        ...(canStaff ? arr(empS).map(e => ({
            type: "STAFF", id: e.employeeId,
            name: `${e.employeeName}${e.employeeCode ? ` (${e.employeeCode})` : ""}`, bal: e.balance,
        })) : []),
        ...(canContractor ? arr(conS).map(c => ({ type: "CONTRACTOR", id: c.contractorId, name: c.contractorName, bal: c.balance })) : []),
        ...(canHundekari ? arr(hunS).map(h => ({ type: "HUNDEKARI", id: h.hundekariId, name: h.hundekariName, bal: h.balance })) : []),
        ...(canTransport ? arr(traS).map(x => ({ type: "TRANSPORT", id: x.transportId, name: x.transportName, bal: x.balance })) : []),
        ...(canVendor ? arr(venS).map(v => ({ type: "VENDOR", id: v.id, name: v.vendorName, bal: null })) : []),
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [cusS, empS, conS, hunS, traS, venS, canCustomer, canStaff, canContractor, canHundekari, canTransport, canVendor]);

    const partyByKey = useMemo(() => {
        const m = {};
        for (const p of allParties) m[`${p.type}:${p.id}`] = p;
        return m;
    }, [allParties]);

    const partyOptions = useMemo(() => allParties
        .filter(p => fTypes.length === 0 || fTypes.includes(p.type))
        .map(p => {
            const pt = pendingText(p.type, p.id);
            return {
                value: `${p.type}:${p.id}`,
                label: `${p.name} · ${TYPE_LABELS[p.type]}${pt ? ` · ${pt}` : ""}`,
            };
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
        , [allParties, fTypes, pendS]);

    const [parties, setParties] = useState([]);   // array of "TYPE:id"
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const [sections, setSections] = useState([]); // [{key, type, id, name, rows, opening, closing, credit, debit}]
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);

    // Normalize whatever shape a ledger endpoint returns: a raw array of
    // rows, or { opening, entries, closing } - both exist in this codebase.
    const normalize = (payload) => {
        const body = payload?.data?.data ?? payload?.data ?? payload ?? {};
        const rows = Array.isArray(body) ? body : (Array.isArray(body.entries) ? body.entries : []);
        const opening = Array.isArray(body) ? null : (body.opening != null ? num(body.opening) : null);
        const lastBal = rows.length ? num(rows[rows.length - 1].balance) : 0;
        const closing = Array.isArray(body) ? lastBal : (body.closing != null ? num(body.closing) : lastBal);
        return { rows, opening, closing };
    };

    const fetchOne = async (key) => {
        const [type, pidStr] = key.split(":");
        const pid = Number(pidStr);
        const ranged = from && to;
        switch (type) {
            case "CUSTOMER": return customerApi.ledger(pid, { from: from || undefined, to: to || undefined });
            case "STAFF": return ranged ? employeeJobWorkApi.getLedgerRange(pid, from, to) : employeeJobWorkApi.getLedger(pid);
            case "CONTRACTOR": return ranged ? contractorLedgerApi.getLedgerRange(pid, from, to) : contractorLedgerApi.getLedger(pid);
            case "HUNDEKARI": return ranged ? hundekariLedgerApi.getLedgerRange(pid, from, to) : hundekariLedgerApi.getLedger(pid);
            case "TRANSPORT": return ranged ? transportLedgerApi.getLedgerRange(pid, from, to) : transportLedgerApi.getLedger(pid);
            case "VENDOR": return vendorLedgerApi.ledger({ vendorId: pid, from: from || undefined, to: to || undefined });
            default: return null;
        }
    };

    const load = async () => {
        if (parties.length === 0) { setSections([]); setLoaded(false); return; }
        setLoading(true); setLoaded(false);
        // One section per party, in the order they were picked. A failed
        // fetch becomes an empty section rather than sinking the whole page.
        const results = await Promise.all(parties.map(async key => {
            const p = partyByKey[key];
            const [type, pidStr] = key.split(":");
            try {
                const res = await fetchOne(key);
                const { rows, opening, closing } = normalize(res);
                return {
                    key, type, id: Number(pidStr), name: p?.name || key,
                    rows, opening, closing,
                    credit: rows.reduce((s, r) => s + num(r.credit), 0),
                    debit: rows.reduce((s, r) => s + num(r.debit), 0),
                    failed: false,
                };
            } catch {
                return { key, type, id: Number(pidStr), name: p?.name || key, rows: [], opening: null, closing: 0, credit: 0, debit: 0, failed: true };
            }
        }));
        setSections(results);
        setLoading(false); setLoaded(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [parties]);

    // Grand strip: the sections added up. Openings only add when every
    // section reported one, otherwise the figure would be a half-truth.
    const grand = useMemo(() => {
        const anyOpeningMissing = sections.some(s => s.opening == null);
        return {
            opening: anyOpeningMissing ? null : sections.reduce((s, x) => s + num(x.opening), 0),
            credit: sections.reduce((s, x) => s + x.credit, 0),
            debit: sections.reduce((s, x) => s + x.debit, 0),
            closing: sections.reduce((s, x) => s + num(x.closing), 0),
            rows: sections.reduce((s, x) => s + x.rows.length, 0),
        };
    }, [sections]);

    const browseRowsFor = (t) => allParties.filter(p => p.type === t)
        .map(p => ({ ...p, pendText: pendingText(p.type, p.id), pendAmt: pendingAmount(p.type, p.id) }))
        .sort((a, b) => (b.pendAmt - a.pendAmt) || (num(b.bal) - num(a.bal)) || a.name.localeCompare(b.name));

    const chip = (label, value, color, big, prefix) => (
        <div style={{ flex: 1, minWidth: big ? 140 : 120, background: "var(--bg-input)", border: `1px solid ${T.border}`, borderRadius: 8, padding: big ? "10px 12px" : "8px 10px" }}>
            <div style={{ fontSize: big ? 11 : 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: T.text2 }}>{label}</div>
            <div style={{ fontSize: big ? 17 : 14, fontWeight: 800, marginTop: 2, color: color || "var(--text-0)" }}>{prefix ? `${prefix}${money(value)}` : money(value)}</div>
        </div>
    );

    const th = { padding: "8px 12px", fontSize: 11, fontWeight: 700, color: T.text2, textTransform: "uppercase", borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap", textAlign: "left" };
    const td = { padding: "8px 12px", borderBottom: `1px solid ${T.border}`, fontSize: 13, verticalAlign: "top" };

    return (
        <div className="fade-up" style={{ padding: "20px 24px" }}>
            <PageHeader
                title="Ledger"
                subtitle="Every ledger in one place — pick one party or many, each with its own opening, credit, debit and balance."
                breadcrumb={["Finance", "Ledger"]}
            />

            <Card>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", padding: "14px 16px", borderBottom: `1px solid ${T.border}` }}>
                    {[{ key: "", label: "All" }, ...TYPES].map(t => {
                        const on = t.key === "" ? fTypes.length === 0 : fTypes.includes(t.key);
                        return (
                            <button key={t.key} type="button"
                                onClick={() => t.key === ""
                                    ? setFTypes([])
                                    : setFTypes(prev => prev.includes(t.key) ? prev.filter(x => x !== t.key) : [...prev, t.key])}
                                style={{
                                    padding: "7px 16px", borderRadius: 20, cursor: "pointer", fontSize: 13, fontWeight: 700,
                                    border: `1px solid ${on ? "var(--primary)" : T.border}`,
                                    background: on ? "var(--primary-light, var(--bg-hover))" : T.bgCard,
                                    color: on ? "var(--primary)" : T.text1, transition: "all 0.15s",
                                }}>{t.label}</button>
                        );
                    })}
                    {parties.length > 0 && (
                        <button type="button" onClick={() => setParties([])}
                            style={{ marginLeft: "auto", border: "none", background: "none", cursor: "pointer", color: T.text2, fontSize: 12, fontWeight: 700 }}>
                            Clear selection
                        </button>
                    )}
                </div>

                <div style={{ padding: "14px 16px", borderBottom: `1px solid ${T.border}`, display: "grid", gridTemplateColumns: "minmax(280px, 2fr) 1fr 1fr auto", gap: 12, alignItems: "end" }}>
                    <Select2 label="Party" value={parties[0] || ""}
                        onChange={v => setParties(v ? [v] : [])}
                        placeholder="Search customer, staff, contractor, hundekari, transport, vendor…"
                        options={partyOptions} />
                    <Input label="From" type="date" value={from} onChange={setFrom} />
                    <Input label="To" type="date" value={to} onChange={setTo} />
                    <Btn onClick={load} disabled={parties.length === 0}>Apply</Btn>
                </div>

                {parties.length === 0 && fTypes.length > 0 && (
                    <div style={{ padding: 16 }}>
                        {fTypes.map(t => {
                            const tot = typeTotals[t] || {};
                            const rowsT = browseRowsFor(t);
                            const isCust = t === "CUSTOMER";
                            return (
                                <div key={t} style={{ marginBottom: 22 }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                                        <span style={{ fontSize: 14, fontWeight: 800, color: T.text0 }}>
                                            {TYPE_LABELS[t]}s <span style={{ color: T.text2, fontWeight: 400 }}>· {rowsT.length}</span>
                                        </span>
                                    </div>
                                    {/* Whole-book figures for this type - the same ledgers, added up. */}
                                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
                                        {chip("Total Debit", tot.debit || 0, "var(--danger)", false, "−")}
                                        {chip("Total Credit", tot.credit || 0, "var(--success)", false, "+")}
                                        {chip(isCust ? "Outstanding" : "Balance", tot.balance || 0,
                                            num(tot.balance) > 0 ? (isCust ? "var(--danger)" : "var(--text-0)") : "var(--success)")}
                                    </div>
                                    {rowsT.length === 0 && (
                                        <div style={{ padding: 18, textAlign: "center", color: T.text2, fontSize: 13, border: `1px dashed ${T.border}`, borderRadius: 8 }}>
                                            No {TYPE_LABELS[t].toLowerCase()}s found.
                                        </div>
                                    )}
                                    {rowsT.length > 0 && (
                                        <div style={{ border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
                                            <div style={{ maxHeight: 340, overflowY: "auto" }}>
                                                {rowsT.map((p, i) => (
                                                    <div key={`${p.type}${p.id}`} onClick={() => setParties([`${p.type}:${p.id}`])}
                                                        style={{
                                                            display: "grid", gridTemplateColumns: "minmax(0,1fr) auto auto", gap: 12, alignItems: "center",
                                                            padding: "10px 14px", borderTop: i === 0 ? "none" : `1px solid ${T.border}`,
                                                            cursor: "pointer", fontSize: 13,
                                                        }}>
                                                        <span style={{ fontWeight: 600, color: T.text0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</span>
                                                        <span style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                                                            {p.pendText
                                                                ? <span style={{ color: "var(--danger)", fontWeight: 700 }}>{p.pendText} · {money(p.pendAmt)}</span>
                                                                : <span style={{ color: T.text2 }}>nothing pending</span>}
                                                        </span>
                                                        <span style={{ whiteSpace: "nowrap", textAlign: "right", minWidth: 120 }}>
                                                            {p.bal != null
                                                                ? <span style={{ fontWeight: 700, color: num(p.bal) > 0 ? "var(--danger)" : "var(--success)" }}>Bal {money(p.bal)}</span>
                                                                : <span style={{ color: T.text2 }}>—</span>}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        <div style={{ fontSize: 12, color: T.text2 }}>
                            Click a name to open that ledger. Section figures are the whole book for the type — every party's ledger added up.
                        </div>
                    </div>
                )}

                {parties.length === 0 && fTypes.length === 0 && (
                    <div style={{ padding: 40, textAlign: "center", color: T.text2, fontSize: 13 }}>
                        Pick a party — or tick type chips (Staff, Contractor, Vendor…) to see each section's Debit, Credit and Balance.
                    </div>
                )}

                {loading && <div style={{ padding: 30, textAlign: "center", color: T.text2, fontSize: 13 }}>Loading ledgers…</div>}

                {!loading && parties.length > 0 && sections.length > 0 && (
                    <div style={{ padding: 16 }}>
                        {/* Grand strip — only when more than one party is on screen;
                for a single party the section header already says it. */}
                        {sections.length > 1 && (
                            <div style={{ marginBottom: 16 }}>
                                <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.03em", color: T.text2, marginBottom: 6 }}>
                                    All {sections.length} parties · {grand.rows} entries
                                </div>
                                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                    {grand.opening != null && chip("Opening", grand.opening, null, true)}
                                    {chip("Total Debit", grand.debit, "var(--danger)", true, "−")}
                                    {chip("Total Credit", grand.credit, "var(--success)", true, "+")}
                                    {chip("Net Balance", grand.closing, num(grand.closing) > 0 ? "var(--danger)" : "var(--success)", true)}
                                </div>
                            </div>
                        )}

                        {/* One section per party: its own summary, then its rows. */}
                        {sections.map(sec => (
                            <div key={sec.key} style={{ marginBottom: 20 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                                    <span style={{ fontSize: 14, fontWeight: 700, color: T.text0 }}>
                                        {sec.name} <span style={{ color: T.text2, fontWeight: 400 }}>· {TYPE_LABELS[sec.type]}</span>
                                    </span>
                                    {pendingText(sec.type, sec.id) && (
                                        <span style={{ fontSize: 12, fontWeight: 700, color: "var(--danger)" }}>
                                            {pendingText(sec.type, sec.id)} · {money(pendingAmount(sec.type, sec.id))}
                                        </span>
                                    )}
                                </div>

                                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
                                    {sec.opening != null && chip("Opening", sec.opening)}
                                    {chip("Total Debit", sec.debit, "var(--danger)", false, "−")}
                                    {chip("Total Credit", sec.credit, "var(--success)", false, "+")}
                                    {chip(sec.type === "CUSTOMER" ? "Outstanding" : "Closing Balance", sec.closing,
                                        num(sec.closing) > 0 ? (sec.type === "CUSTOMER" ? "var(--danger)" : "var(--text-0)") : "var(--success)")}
                                </div>

                                {sec.rows.length === 0 && (
                                    <div style={{ padding: 18, textAlign: "center", color: T.text2, fontSize: 13, border: `1px dashed ${T.border}`, borderRadius: 8 }}>
                                        {sec.failed ? "Could not load this ledger." : `No entries${from && to ? " in this period" : ""}.`}
                                    </div>
                                )}

                                {sec.rows.length > 0 && (
                                    <div style={{ border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
                                        <div style={{ maxHeight: sections.length > 1 ? 380 : 560, overflowY: "auto" }}>
                                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                <thead>
                                                    <tr style={{ background: "var(--bg-input)", position: "sticky", top: 0, zIndex: 1 }}>
                                                        <th style={th}>Date</th>
                                                        <th style={th}>Particular</th>
                                                        <th style={th}>Ref / No</th>
                                                        <th style={{ ...th, textAlign: "right" }}>Debit</th>
                                                        <th style={{ ...th, textAlign: "right" }}>Credit</th>
                                                        <th style={{ ...th, textAlign: "right" }}>Balance</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {sec.rows.map((r, i) => (
                                                        <tr key={r.id != null ? `id${r.id}` : `i${i}`}>
                                                            <td style={{ ...td, whiteSpace: "nowrap" }}>{fmtDate(r.entryDate || r.date)}</td>
                                                            <td style={{ ...td, color: T.text0 }}>
                                                                {r.particular || "—"}
                                                                {r.notes && <div style={{ fontSize: 11, color: T.text2, marginTop: 2 }}>{r.notes}</div>}
                                                            </td>
                                                            <td style={{ ...td, fontFamily: "monospace", fontSize: 11, color: "var(--primary)", whiteSpace: "nowrap" }}>
                                                                {r.paymentNumber || r.paymentReference || r.refNo || "—"}
                                                            </td>
                                                            <td style={{ ...td, textAlign: "right", fontWeight: 600, color: num(r.debit) > 0 ? "var(--danger)" : T.text2 }}>
                                                                {num(r.debit) > 0 ? `−${money(r.debit)}` : "—"}
                                                            </td>
                                                            <td style={{ ...td, textAlign: "right", fontWeight: 600, color: num(r.credit) > 0 ? "var(--success)" : T.text2 }}>
                                                                {num(r.credit) > 0 ? `+${money(r.credit)}` : "—"}
                                                            </td>
                                                            <td style={{ ...td, textAlign: "right", fontWeight: 800 }}>{money(r.balance)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {!loading && loaded && parties.length > 0 && sections.length === 0 && (
                    <div style={{ padding: 30, textAlign: "center", color: T.text2, fontSize: 13 }}>Nothing to show.</div>
                )}
            </Card>
        </div>
    );
}